import type { AppContextStates } from '@service-victoria/component-library/components/Wizard/props';
import { AxiosError } from 'axios';
import { FormikProps } from 'formik';
import { startCase } from 'lodash-es';
import * as Interfaces from 'app/interfaces';
import {
  BulkVoucherRedemptionRequest,
  BulkVoucherRedemptionResponse,
  FetchProvidersResponse,
  IndividualVoucherRedemptionRequest,
  IndividualVoucherResult,
  ProviderDetailResponse,
  VoucherErrorType,
  VoucherRedemptionResponse
} from 'app/interfaces/api';
import {
  VoucherErrorMessage,
  BulkAggregateSummary,
  CSVErrorMessage,
  TransactionType
} from 'app/interfaces/common';
import { AppContext } from 'app/interfaces/store';
import { api, constants, logger } from 'app/utils';
import { MAX_RETRIES } from 'app/utils/constants';
import { userManager } from '..';

type FormPropsRedeemIndividual = FormikProps<{
  redeem: Pick<Interfaces.Common.RedemptionFormValues['redeem'], 'individual'>;
}>;
type FormPropsRedeemBulk = FormikProps<{
  redeem: Pick<Interfaces.Common.RedemptionFormValues['redeem'], 'bulk'>;
}>;
type FormPropsReview = FormikProps<Pick<Interfaces.Common.RedemptionFormValues, 'review'>>;

const apiAdapter = new api.Adapter({
  baseURL: window.CONFIG.gakAPIDomain,
  responseType: 'json',
  headers: {
    'x-api-key': window.CONFIG.gakAPIKey ?? ''
  }
});

const reactAppApiKey = window.CONFIG.gakAPIKey ?? '';

export async function submitProviderEditRequest<T extends Record<string, any>>(
  value: T,
  retryCount = 0
): Promise<string> {
  const user = await userManager.getUserData();
  if (!user?.access_token) {
    throw new Error('Access token not issued to user');
  }

  try {
    const {
      data: { message },
      status
    } = await apiAdapter.patchValue<any>('/provider-edit', value, {
      headers: { authorization: `Bearer ${user?.access_token}`, 'x-api-key': reactAppApiKey }
    });

    if (status === 200) {
      return 'SUCCESS';
    }

    console.log('Form submission error - status', status);
    console.log('Form submission error - message', message);

    return 'TECHNICAL_ERROR';
  } catch (error) {
    const isAuthError =
      error instanceof AxiosError && [401, 403].includes(error.response?.status || 200);

    if (retryCount >= MAX_RETRIES) {
      logger.error('Error querying /provider-edit', error);

      throw error;
    }

    if (isAuthError) {
      await userManager.refreshToken();
    }

    return submitProviderEditRequest<T>(value, retryCount + 1);
  }
}

async function postValueWithRetry<TRes>(url: string, req: any, retryCount = 1): Promise<TRes> {
  try {
    const user = await userManager.getUserData();

    if (!user?.access_token) {
      throw new Error('Access token not issued to user');
    }

    const { data } = await apiAdapter.postValue<TRes>(url, req, {
      headers: {
        authorization: `Bearer ${user.access_token}`
      }
    });

    return data;
  } catch (error: any) {
    const isAuthError =
      error instanceof AxiosError && [401, 403].includes(error.response?.status || 200);
    const sleep = () => new Promise(resolve => setTimeout(resolve, 1000));

    if (retryCount >= MAX_RETRIES) {
      logger.error('Error querying /voucher-redemption', error);

      throw error;
    }

    if (isAuthError) {
      await userManager.refreshToken();
    } else {
      await sleep();
    }

    return await postValueWithRetry<TRes>(url, req, retryCount + 1);
  }
}

export async function postIndividualVoucherRedemption(
  body: IndividualVoucherRedemptionRequest
): Promise<VoucherRedemptionResponse> {
  return await postValueWithRetry<VoucherRedemptionResponse>('/voucher-redemption', body);
}

export async function postBulkVoucherRedemption(
  body: BulkVoucherRedemptionRequest
): Promise<BulkVoucherRedemptionResponse> {
  return await postValueWithRetry<BulkVoucherRedemptionResponse>('/voucher-redemption-bulk', body);
}

const prepareVouchersForSubmission = (
  payload: IndividualVoucherRedemptionRequest
): IndividualVoucherRedemptionRequest => {
  const vouchers = payload.vouchers.map(voucher => {
    return {
      ...voucher,
      amount: voucher.amount.replace('$', '')
    };
  });

  return {
    ...payload,
    vouchers
  };
};

const getBulkRedemptionAggregateData = (
  payload: BulkVoucherRedemptionResponse
): BulkAggregateSummary => {
  if (!Array.isArray(payload.vouchers) || payload.vouchers.length === 0) {
    throw new Error('No vouchers found in response');
  }

  const totalVouchers = payload.vouchers.length;
  let totalAmount = payload.vouchers.reduce((accum, voucher) => {
    if (!voucher.amount) return accum;

    const parsedAmount = Number(voucher.amount);
    if (Number.isNaN(parsedAmount))
      throw new Error(`Unable to parse voucher amount "${voucher.amount}" on row ${voucher.row}`);

    accum = accum + parsedAmount;

    return accum;
  }, 0);
  const uniqueActivityTypes = payload.vouchers.reduce((accum, voucher) => {
    if (!voucher.activityType) return accum;

    const parsedActivityType = startCase(voucher.activityType.trim().toLowerCase());
    accum.add(parsedActivityType);

    return accum;
  }, new Set<string>());

  // parse to 2 decimal places
  totalAmount = parseFloat(totalAmount.toFixed(2));

  return {
    totalVouchers,
    totalAmount,
    activityTypes: [...uniqueActivityTypes]
  };
};

/**
 * @description
 * Prepares a payload to query the individual voucher redemption endpoint.
 * Sets appropriate error states as required
 */
export const validateOrRedeemIndividualVoucher = async (
  values: Interfaces.Common.Redeem['individual'],
  formProps: FormPropsRedeemIndividual | FormPropsReview,
  appStates: AppContextStates,
  validateOnly: boolean
): Promise<IndividualVoucherResult> => {
  formProps.setStatus(null);
  const appContext = appStates[constants.RedemptionStorageKeys.State] as AppContext;
  const { ID } = appContext.currentState.transactions[TransactionType.Redemption];
  const { idToken } = appContext.currentState;
  const { voucherCode, childName, activityType, amount } = values;

  let response: IndividualVoucherResult = {
    voucherCode,
    success: false,
    orgName: ''
  };

  const payload: IndividualVoucherRedemptionRequest = {
    transactionId: ID,
    validateOnly: validateOnly,
    idamIdToken: idToken?.rawToken || '',
    vouchers: [
      {
        voucherCode,
        childName,
        activity: activityType,
        amount
      }
    ]
  };
  const preparedPayload = prepareVouchersForSubmission(payload);
  try {
    const result = await postIndividualVoucherRedemption(preparedPayload);
    response = {
      ...result.vouchers[0]
    };

    if (response.success) {
      formProps.setStatus({ voucher: response, error: '' });
    } else {
      const errorType = response.errorType ?? VoucherErrorType.SERVER_ERROR;
      formProps.setStatus({ voucher: response, error: VoucherErrorMessage[errorType] });
    }
  } catch (error) {
    formProps.setStatus({ voucher: response, error: VoucherErrorMessage.SERVER_ERROR });
  }

  return response;
};

/**
 * @description
 * Prepares a payload to query the bulk voucher redemption endpoint.
 * Sets appropriate error states as required
 */
export const validateOrRedeemBulkVouchers = async (
  values: Interfaces.Common.Redeem['bulk'],
  formProps: FormPropsRedeemBulk | FormPropsReview,
  appStates: AppContextStates,
  validateOnly: boolean
): Promise<BulkAggregateSummary | false> => {
  formProps.setStatus(null);
  const appContext = appStates[constants.RedemptionStorageKeys.State] as AppContext;
  const { ID } = appContext.currentState.transactions[TransactionType.Redemption];
  const { idToken } = appContext.currentState;
  const csvFile = values.fileMapping[0];

  const payload: BulkVoucherRedemptionRequest = {
    transactionId: ID,
    validateOnly: validateOnly,
    idamIdToken: idToken?.rawToken || '',
    csv: csvFile
  };

  try {
    const response = await postBulkVoucherRedemption(payload);
    const success =
      response.success === true &&
      Array.isArray(response.vouchers) &&
      response.vouchers.length > 0 &&
      !response.vouchers.some(voucher => !voucher.success);

    if (success) {
      const aggregateData = getBulkRedemptionAggregateData(response);

      formProps.setStatus({ error: '', bulkVouchers: {} });
      return aggregateData;
    } else {
      const errors = (response.vouchers || []).flatMap(voucher => {
        return voucher.errorType
          ? [{ row: voucher.row, error: VoucherErrorMessage[voucher.errorType] }]
          : [];
      });
      formProps.setStatus({
        error: response.errorMessage
          ? CSVErrorMessage[response.errorMessage || ''] || VoucherErrorMessage.SERVER_ERROR
          : undefined,
        bulkVouchers: { success: false, errors }
      });
    }
  } catch (error) {
    console.error(error);
    formProps.setStatus({ error: VoucherErrorMessage.SERVER_ERROR, bulkVouchers: {} });
  }
  return false;
};

/**
 * @description
 * Returns a boolean indicating whether a given user's customer ID is linked to a registered provider.
 */
export const isUserLinkedToRegisteredProvider = async (idamIdToken: string): Promise<boolean> => {
  try {
    await apiAdapter.postValue<unknown>('/fetch-providers', { idamIdToken });

    // If the request was successful, assume the user is linked to a registered provider.
    return true;
  } catch (error: any) {
    console.error(error);
    if (error.message.includes('Request failed with status code 404')) {
      // If the service returns a 404, the user is not linked to a registered provider.
      return false;
    } else {
      // Something is broken, rethrow the error.
      throw error;
    }
  }
};

export async function fetchProviders<T extends Record<string, any>>(
  retryCount = 0
): Promise<FetchProvidersResponse> {
  const user = await userManager.getUserData();
  if (!user?.access_token) {
    throw new Error('Access token not issued to user');
  }

  try {
    const { data } = await apiAdapter.postValue<FetchProvidersResponse>(
      '/fetch-providers',
      {
        idamIdToken: user.id_token
      },
      {
        headers: { authorization: `Bearer ${user?.access_token}` }
      }
    );

    return data;
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 404) {
      // If the service returns a 404, the user is not linked to a registered provider.
      return [];
    }

    const isAuthError =
      error instanceof AxiosError && [401, 403].includes(error.response?.status || 200);

    if (retryCount >= MAX_RETRIES) {
      logger.error('Error querying /providers', error);

      throw error;
    }

    if (isAuthError) {
      await userManager.refreshToken();
    }

    return fetchProviders<T>(retryCount + 1);
  }
}

export async function getProviderDetail<T extends Record<string, any>>(
  providerId: string,
  retryCount = 0
): Promise<ProviderDetailResponse> {
  const user = await userManager.getUserData();
  if (!user?.access_token) {
    throw new Error('Access token not issued to user');
  }

  try {
    const { data } = await apiAdapter.postValue<ProviderDetailResponse>(
      '/providers/detail',
      {
        idamIdToken: user.id_token,
        providerId
      },
      {
        headers: { authorization: `Bearer ${user?.access_token}` }
      }
    );

    return data;
  } catch (error) {
    const isAuthError =
      error instanceof AxiosError && [401, 403].includes(error.response?.status || 200);

    if (retryCount >= MAX_RETRIES) {
      logger.error('Error querying /providers', error);

      throw error;
    }

    if (isAuthError) {
      await userManager.refreshToken();
    }

    return getProviderDetail<T>(providerId, retryCount + 1);
  }
}
